// src/pages/ViewReportPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { formatRut, formatPhone } from '../utils/formatters';
import { reportService } from '../services/reportService';
import { usePermissions } from '../hooks/usePermissions';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

// Heroicons
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PrinterIcon, EnvelopeIcon, TrashIcon, PencilSquareIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';

// --- UTILS ---
const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('es-CL');
};

// --- COMPONENTES AUXILIARES ---

const ReadOnlyField = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? 'md:col-span-3 print:col-span-3' : ''}>
        <p className="block text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 print:text-gray-500 mb-1">{label}</p>
        <div className="text-sm text-gray-900 dark:text-gray-100 print:text-gray-900 bg-gray-50 dark:bg-gray-700 print:bg-gray-50 p-2 rounded border border-gray-100 dark:border-gray-600 print:border-gray-200 min-h-[2.5rem] whitespace-pre-wrap">
            {value || '-'}
        </div>
    </div>
);

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm transition-all">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-2xl border border-gray-100 dark:border-gray-700 transform scale-100 animate-in fade-in zoom-in duration-200">
                <div className="flex items-center mb-4 text-red-600 dark:text-red-400 gap-3">
                    <ExclamationTriangleIcon className="w-8 h-8" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed">{message}</p>
                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 font-medium transition-colors">Cancelar</button>
                    <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold shadow-sm transition-colors flex items-center gap-2">
                        <TrashIcon className="w-4 h-4" /> Sí, Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
};

const EmailModal = ({ visit, visitId, onClose, setNotification, companyName }) => {
    const { user } = useAuth();
    const [recipient, setRecipient] = useState(visit.email || '');
    const [cc, setCc] = useState('');
    const [sending, setSending] = useState(false);

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setSending(true);

        const payload = {
            reportId: visitId,
            emailTo: recipient,
            emailCc: cc,
            emailSubject: `Informe Técnico #${visitId} - ${companyName}`,
            emailBody: `
                <div style="font-family: Arial, sans-serif; padding: 20px;">
                    <h2>Informe Técnico de Visita</h2>
                    <p>Estimado/a <b>${visit.clientName}</b>,</p>
                    <p>Adjuntamos a este correo el informe técnico detallado correspondiente a la intervención folio <b>#${visitId}</b>, realizada el ${formatDate(visit.fecha)}.</p>
                    <p>El documento incluye el detalle de los trabajos realizados, repuestos utilizados y fotografías de respaldo adjuntas en alta calidad PDF.</p>
                    <br><p>Saludos cordiales,<br><b>Servicio Técnico - ${companyName}</b></p>
                </div>
            `,
            isQuote: false
        };

        try {
            const generateAndSendPDF = httpsCallable(functions, 'generateAndSendPDF');
            const result = await generateAndSendPDF(payload);
            setNotification({ message: 'Informe PDF generado y enviado con éxito.', type: 'success' });
            onClose();
        } catch (error) {
            console.error("Cloud Functions Error:", error);
            setNotification({ message: `Error al enviar el PDF: ${error.message}`, type: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 w-full max-w-lg shadow-2xl animate-in fade-in zoom-in duration-200 border border-transparent dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6 text-blue-600 dark:text-blue-400">
                    <EnvelopeIcon className="w-6 h-6" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Enviar Informe</h2>
                </div>
                <form onSubmit={handleSendEmail}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Para (Cliente):</label>
                        <input type="email" value={recipient} onChange={e => setRecipient(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC (Copia a):</label>
                        <input type="email" value={cc} onChange={e => setCc(e.target.value)} placeholder="correo@ejemplo.com" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-6 bg-blue-50 dark:bg-gray-700 p-2 rounded text-center">Una copia de respaldo puede llegar a su correo interno.</p>
                    <div className="flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">Cancelar</button>
                        <button type="submit" disabled={sending} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 font-medium shadow-md transition-all flex items-center gap-2">
                            {sending ? 'Enviando...' : 'Enviar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ReportContent = ({ visitData }) => (
    <div className="space-y-8 print:space-y-6">
        {/* I. DATOS CLIENTE */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">I</span>
                Datos del Cliente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-6 print:gap-4">
                <ReadOnlyField label="Fecha" value={formatDate(visitData.fecha)} />
                <ReadOnlyField label="RUT del Cliente" value={formatRut(visitData.clientId)} />
                <ReadOnlyField label="Cliente / Empresa" value={visitData.clientName} />
                <ReadOnlyField label="Dirección" value={visitData.direccion} fullWidth />
                <ReadOnlyField label="Encargado" value={visitData.encargado} />
                <ReadOnlyField label="Teléfono" value={formatPhone(visitData.telefono)} />
                <ReadOnlyField label="Email" value={visitData.email} />
            </div>
        </div>
        {/* II. INFORMACIÓN DE LA VISITA */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">II</span>
                Información de la Visita
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-6 print:gap-4">
                <ReadOnlyField label="Hora Inicio" value={visitData.horaInicio} />
                <ReadOnlyField label="Hora Término" value={visitData.horaTermino} />
                <ReadOnlyField label="Técnico Asignado" value={visitData.tecnico} />
                <ReadOnlyField label="Tipo de Visita" value={visitData.tipoVisita} fullWidth />
            </div>
        </div>
        {/* III. DETALLE DE LA INTERVENCIÓN */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">III</span>
                Detalle de la Intervención
            </h2>
            <div className="space-y-4">
                <ReadOnlyField label="Motivo / Problema" value={visitData.motivo} />
                <ReadOnlyField label="Diagnóstico Técnico" value={visitData.diagnostico} />
                <ReadOnlyField label="Trabajos Realizados" value={visitData.trabajos} />
            </div>
        </div>
        {/* IV. MATERIALES */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">IV</span>
                Materiales y Repuestos
            </h2>
            {visitData.materiales && visitData.materiales.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 print:border-gray-200">
                    <table className="min-w-full text-sm text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700 print:bg-gray-50 text-gray-700 dark:text-gray-300 print:text-gray-700 font-medium">
                            <tr>
                                <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200">Cant.</th>
                                <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200">Descripción</th>
                                <th className="px-4 py-3 border-b dark:border-gray-600 print:border-gray-200">Código</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 print:divide-gray-200">
                            {visitData.materiales.map((m, i) => (
                                <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/50">
                                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100 print:text-gray-900">{m.cantidad}</td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 print:text-gray-600">{m.descripcion}</td>
                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-500 print:text-gray-500 font-mono text-xs">{m.codigo}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400 print:text-gray-500 italic bg-gray-50 dark:bg-gray-700 print:bg-gray-50 p-4 rounded text-center border border-dashed border-gray-300 dark:border-gray-600 print:border-gray-300">No se registraron materiales utilizados.</div>
            )}
        </div>
        {/* V. ESTADO FINAL */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-blue-800 dark:text-blue-400 print:text-blue-800 uppercase border-b-2 border-blue-100 dark:border-blue-900 print:border-blue-100 flex items-center gap-2">
                <span className="bg-blue-100 dark:bg-blue-900/50 print:bg-blue-100 text-blue-800 dark:text-blue-300 print:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full text-xs">V</span>
                Estado Final y Recomendaciones
            </h2>
            <div className="space-y-4">
                <div className="bg-blue-50/50 dark:bg-blue-900/20 print:bg-blue-50/50 p-4 rounded-lg border border-blue-100 dark:border-blue-800/50 print:border-blue-100">
                    <ReadOnlyField label="Estado Final del Equipo" value={visitData.estadoFinal} />
                </div>
                <ReadOnlyField label="Recomendaciones" value={visitData.recomendaciones} />
            </div>
        </div>
        {/* VI. FIRMA */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 break-inside-avoid transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-gray-700 dark:text-gray-200 print:text-gray-700 border-b dark:border-gray-700 print:border-gray-200 flex items-center gap-2">
                <span className="bg-gray-200 dark:bg-gray-700 print:bg-gray-200 text-gray-700 dark:text-gray-200 print:text-gray-700 w-6 h-6 flex items-center justify-center rounded-full text-xs">VI</span>
                Firma y Conformidad
            </h2>
            <div className="flex justify-center py-6">
                {visitData.firmaDigital ? (
                    <div className="text-center">
                        <img src={visitData.firmaDigital} alt="Firma del cliente" className="border-2 border-dashed border-gray-300 dark:border-gray-600 print:border-gray-300 rounded-lg h-32 mb-2 bg-white dark:bg-gray-200 print:bg-white p-2" />
                        <p className="text-xs text-gray-400 print:text-gray-500 uppercase tracking-widest">Firma Cliente</p>
                    </div>
                ) : (
                    <p className="text-gray-500 dark:text-gray-400 print:text-gray-500 italic bg-gray-50 dark:bg-gray-700 print:bg-gray-50 px-6 py-4 rounded-full">No se registró firma.</p>
                )}
            </div>
        </div>
        {/* VII. FOTOS */}
        <div className="bg-white dark:bg-gray-800 print:bg-white p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 print:border print:border-gray-200 print:break-before-auto transition-colors">
            <h2 className="text-sm font-bold mb-6 pb-2 text-gray-700 dark:text-gray-200 print:text-gray-700 border-b dark:border-gray-700 print:border-gray-200 flex items-center gap-2">
                <span className="bg-gray-200 dark:bg-gray-700 print:bg-gray-200 text-gray-700 dark:text-gray-200 print:text-gray-700 w-6 h-6 flex items-center justify-center rounded-full text-xs">VII</span>
                Registro Fotográfico
            </h2>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 print:grid-cols-4 gap-4">
                {visitData.fotos && visitData.fotos.length > 0 ? visitData.fotos.map((photo, index) => (
                    <div key={index} className="group relative border border-gray-200 dark:border-gray-700 print:border-gray-200 p-1.5 rounded-xl bg-white dark:bg-gray-800 print:bg-white shadow-sm hover:shadow-md break-inside-avoid transition-all">
                        <div className="aspect-square overflow-hidden rounded-lg">
                            <a href={photo.url} target="_blank" rel="noopener noreferrer" className="block w-full h-full cursor-zoom-in">
                                <img src={photo.url} alt={`foto ${index + 1}`} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"/>
                            </a>
                        </div>
                        <div className="mt-2 text-center">
                            <span className="text-xs text-gray-500 font-medium">Foto {index + 1}</span>
                        </div>
                    </div>
                )) : (
                    <div className="col-span-4 text-center py-8 bg-gray-50 dark:bg-gray-700 print:bg-gray-50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 print:border-gray-300">
                        <p className="text-gray-500 dark:text-gray-400 print:text-gray-500 text-sm italic">No se adjuntaron fotos.</p>
                    </div>
                )}
            </div>
        </div>
    </div>
);

// --- COMPONENTE PRINCIPAL ---
const ViewReportPage = ({ visitId, setPage, setNotification }) => {
    // Contextos reales
    const { user } = useAuth();
    const { logoUrl, watermarkUrl, address: companyAddress, email: companyEmail, name: contextCompanyName } = useCompany();
    const { isAdmin, canDo } = usePermissions();

    const [visitData, setVisitData] = useState(null);
    const [parameters, setParameters] = useState({ headerInfo: '', footerInfo: '', companyName: '' });
    const [loading, setLoading] = useState(true);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    const displayCompanyName = contextCompanyName || parameters.companyName || 'NOMBRE EMPRESA';

    // Permisos
    const canEdit = isAdmin || canDo('reports', 'edit');
    const canDelete = isAdmin || canDo('reports', 'delete');

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                // Usamos el servicio real
                const [initData, reportData] = await Promise.all([
                    reportService.getInitialData(),
                    reportService.getVisitById(visitId)
                ]);

                setParameters(initData.parameters);
                setVisitData(reportData);
            } catch (error) {
                console.error("Error fetching data:", error);
                setNotification({ message: 'Error al cargar los datos.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        
        if (visitId) {
            fetchAllData();
        }
    }, [visitId, setNotification]);

    const confirmDelete = async () => {
        setShowDeleteModal(false);
        try {
            await reportService.deleteVisit(visitId, visitData.fotos);
            setNotification({ message: `Informe ${visitId} eliminado correctamente.`, type: 'success' });
            setPage('dashboard');
        } catch (error) {
            console.error("Error deleting report:", error);
            setNotification({ message: 'Error al eliminar.', type: 'error' });
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 dark:text-gray-400 font-medium">Cargando informe...</p>
            </div>
        </div>
    );
    
    if (!visitData) return <div className="p-8 text-center text-red-500">No se pudo cargar la información o el informe no existe.</div>;

    return (
        <div className="min-h-screen bg-gray-100/50 dark:bg-gray-900 print:bg-white pb-12 print:pb-0 print:min-h-0 print:h-auto transition-colors duration-300">
            {showEmailModal && <EmailModal visit={visitData} visitId={visitId} onClose={() => setShowEmailModal(false)} setNotification={setNotification} companyName={displayCompanyName} />}
            
            <ConfirmationModal 
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Eliminar Informe"
                message={`¿Estás seguro de que deseas eliminar el informe ${visitId}? Esta acción no se puede deshacer.`}
            />

            {/* BARRA SUPERIOR (Botones de acción) */}
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-30 px-4 py-3 shadow-sm print:hidden transition-colors">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setPage('dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-500 dark:text-gray-400">
                            <ArrowLeftIcon className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                Detalle Informe
                                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-md">#{visitId}</span>
                            </h1>
                        </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 justify-end w-full md:w-auto">
                        {canEdit && (
                            <button onClick={() => setPage('editForm', visitId)} className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors text-sm font-medium">
                                <PencilSquareIcon className="w-4 h-4" /> <span className="hidden sm:inline">Modificar</span>
                            </button>
                        )}
                        {canDelete && (
                            <button onClick={() => setShowDeleteModal(true)} className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors text-sm font-medium">
                                <TrashIcon className="w-4 h-4" /> <span className="hidden sm:inline">Eliminar</span>
                            </button>
                        )}
                        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1 hidden md:block"></div>
                        <button onClick={() => setShowEmailModal(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 shadow-sm transition-all text-sm font-medium">
                            <EnvelopeIcon className="w-4 h-4" /> Enviar Correo
                        </button>
                        <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-slate-800 dark:bg-slate-700 text-white rounded-lg hover:bg-slate-900 dark:hover:bg-slate-600 shadow-sm transition-all text-sm font-medium">
                            <PrinterIcon className="w-4 h-4" /> Imprimir
                        </button>
                    </div>
                </div>
            </div>

            {/* VISTA DEL INFORME (Visible en pantalla y papel conservando formato) */}
            <div className="max-w-5xl mx-auto mt-6 md:p-8 print:p-4 bg-white dark:bg-gray-800 print:bg-white md:shadow-lg md:rounded-xl md:border border-transparent dark:border-gray-700 transition-colors print:shadow-none print:border-0 print:m-0 print:w-full print:max-w-none text-gray-900 dark:text-gray-100 print:text-gray-900">
                
                {/* Cabecera */}
                <div className="relative z-10 p-6 md:p-0 print:p-0">
                    <div className="flex justify-between border-b-2 border-gray-800 dark:border-gray-500 print:border-gray-800 pb-6 mb-8 items-start">
                        <div className="flex items-center gap-6">
                            {logoUrl ? (
                                <img src={logoUrl} className="h-20 w-auto object-contain bg-white rounded p-1" alt="Logo"/>
                            ) : (
                                <div className="h-20 w-20 bg-gray-200 dark:bg-gray-700 print:bg-gray-200 flex items-center justify-center rounded text-xs text-gray-500 dark:text-gray-400 print:text-gray-500">LOGO</div>
                            )}
                            <div className="text-xs text-gray-600 dark:text-gray-300 print:text-gray-600 space-y-1 hidden sm:block print:block">
                                <p className="font-bold text-gray-800 dark:text-gray-100 print:text-gray-800 text-sm uppercase">{displayCompanyName}</p>
                                <p className="whitespace-pre-line">{companyAddress || parameters.headerInfo}</p>
                                <p>{companyEmail}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <h1 className="text-2xl md:text-3xl print:text-3xl font-extrabold text-gray-900 dark:text-gray-100 print:text-gray-900 tracking-tight">INFORME DE VISITA</h1>
                            <div className="mt-2 inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-700 print:bg-gray-100 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600 print:border-gray-200">
                                <span className="text-gray-500 dark:text-gray-400 print:text-gray-500 text-xs font-bold uppercase">Folio</span>
                                <span className="text-gray-900 dark:text-gray-100 print:text-gray-900 font-mono font-bold">{visitId}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Contenido Modularizado */}
                    <ReportContent visitData={visitData} />
                    
                    {/* Pie de Página */}
                    <div className="mt-12 print:mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 print:border-gray-200 flex flex-col items-center text-center gap-2">
                        <p className="text-xs text-gray-400 dark:text-gray-500 print:text-gray-500 whitespace-pre-line">{parameters.footerInfo}</p>
                        <p className="text-[10px] text-gray-300 dark:text-gray-600 print:text-gray-400">Generado el {new Date().toLocaleDateString()}</p>
                    </div>
                </div>
                
                {/* Marca de Agua */}
                {watermarkUrl && (
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-0 overflow-hidden print:fixed print:inset-0">
                        <img src={watermarkUrl} className="w-[80%] opacity-[0.03] transform -rotate-45" alt="Marca de agua" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewReportPage;